import {
    AccountCategory,
    type AccountSummary,
    AccountType,
    ApexDerivedSubState,
    type CategorySummary,
    type HolisticSummaryCategory,
    LinkState,
    Provider,
    ReadableApexAccountRegistrationType,
    SpendAccountCategory,
    SupportedApexAccountRegistrationType,
} from '@fruitful/types';
import { useAtomValue } from 'jotai';
import capitalize from 'lodash/capitalize';
import { Button as PRButton } from 'primereact/button';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableDataSelectableEvent,
    type DataTableSelectEvent,
} from 'primereact/datatable';
import { Fieldset } from 'primereact/fieldset';
import React, { useMemo, useState } from 'react';
import {
    BadgeLabel,
    BanknoteIcon,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    ChevronDownIcon,
    ChevronRightIcon,
    CircleAlertIcon,
    CircleQuestionMarkIcon,
    CreditCardIcon,
    EditIcon,
    FootnoteRegular,
    FruitfulLogoIcon,
    HourglassIcon,
    Icon,
    IconButton,
    type IconType,
    LabelRegular,
    LandmarkIcon,
    LinkIcon,
    MergeIcon,
    PiggyBankIcon,
    PlusIcon,
    Spinner,
    tokens,
    Tooltip,
    TooltipTrigger,
    TrashIcon,
    TrendingUpIcon,
    UserCheckIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { isEmpty } from 'smoothie/src/misc';

import Logo from '../../../../assets/images/fruitful_logo.svg';
import {
    MonospaceDetailText,
    MonospaceNumericText,
} from '../../../common/components/GridCellRenderers';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { TextError } from '../../../common/components/InlineError';
import {
    centsToFormattedDollars,
    formatToSimpleDateString,
} from '../../../common/utils/formatting';
import { adminUserAtom } from '../../entityAtoms';
import {
    AdminUserRole,
    type MemberDetails,
    type MembershipDetails,
    MembershipType,
} from '../../entityTypes';
import { useMembershipHolisticSummary } from '../hooks/useMembershipHolisticSummary';
import { AccountCreateModal } from './AccountCreateModal';
import { AccountDeleteModal } from './AccountDeleteModal';
import { AccountEditModal } from './AccountEditModal';
import { AccountMergeModal } from './AccountMergeModal';
import { ApexDepositModal } from './ApexDepositModal';
import { ApexWithdrawalModal } from './ApexWithdrawalModal';
import { InvestmentAccountTable } from './investment/InvestmentAccountTable';

const AccountCategoryIcons: Record<HolisticSummaryCategory, IconType> = {
    [AccountCategory.SPEND]: CreditCardIcon,
    [AccountCategory.SAVE]: PiggyBankIcon,
    [AccountCategory.INVEST]: TrendingUpIcon,
    [AccountCategory.BORROW]: LandmarkIcon,
};

const SpendCategoryIcons: Record<SpendAccountCategory, IconType> = {
    [SpendAccountCategory.CASH]: BanknoteIcon,
    [SpendAccountCategory.CREDIT]: CreditCardIcon,
};

export const AccountsTabPanel = ({
    membershipDetails,
}: {
    membershipDetails: MembershipDetails;
}) => {
    const [editingAccount, setEditingAccount] = useState<AccountSummary | null>(
        null,
    );
    const [deletingAccount, setDeletingAccount] =
        useState<AccountSummary | null>(null);
    const [mergingAccount, setMergingAccount] = useState<AccountSummary | null>(
        null,
    );
    const [apexWithdrawalAccount, setApexWithdrawalAccount] =
        useState<AccountSummary | null>(null);
    const [apexDepositAccount, setApexDepositAccount] =
        useState<AccountSummary | null>(null);
    const [selectedCreateAccountCategory, setSelectedCreateAccountCategory] =
        useState<AccountCategory | null>(null);

    const { isLoadingHolisticSummary, isHolisticSummaryError } =
        useMembershipHolisticSummary(membershipDetails.id);

    // Check if any user on the membership has a spinwheelId (indicating they've signed up for Spinwheel)
    // This must be called before any early returns to avoid hook order issues
    const hasAnySpinwheelAccounts = useMemo(() => {
        return membershipDetails.users.some((user) => {
            const userWithSpinwheelId = user as MemberDetails & {
                spinwheelId?: string | null;
            };
            return (
                userWithSpinwheelId.spinwheelId != null &&
                userWithSpinwheelId.spinwheelId !== ''
            );
        });
    }, [membershipDetails.users]);

    if (isHolisticSummaryError) {
        return <TextError message="Error loading accounts data" />;
    }

    if (isLoadingHolisticSummary) {
        return (
            <XStack alignItems="center" gap={8}>
                <View>
                    <Spinner />
                </View>
                <BodyMediumRegular fontStyle="italic">
                    Loading holistic data...
                </BodyMediumRegular>
            </XStack>
        );
    }

    const holisticSummary = membershipDetails.holisticSummary;

    // TODO It would be a better fix to do this on the backend but right now that
    //   refactor is annoying so I'm doing it here as a quick fix.
    const investAccts = holisticSummary?.invest.accounts;
    const openInvestAccounts =
        investAccts?.filter((a: AccountSummary) => {
            // Only show OPEN Apex accounts
            return a.provider === Provider.APEX
                ? a.apexAccount?.derivedSubState ===
                      ApexDerivedSubState.OPEN_ACTIVE
                : true;
        }) ?? [];

    if (
        holisticSummary?.invest.accounts != null &&
        openInvestAccounts.length > 0
    ) {
        holisticSummary.invest.accounts = openInvestAccounts;
    }

    const totalNumberOfAccounts =
        (holisticSummary?.spend?.accounts.length ?? 0) +
        (holisticSummary?.save?.accounts.length ?? 0) +
        (holisticSummary?.invest?.accounts.length ?? 0) +
        (holisticSummary?.borrow?.accounts.length ?? 0);

    return (
        <YStack>
            <XStack width="100%" justifyContent="flex-end" marginTop={-8}>
                <BodySmallEmphasized>
                    {isNaN(totalNumberOfAccounts) ? 0 : totalNumberOfAccounts}{' '}
                    accounts for this membership
                </BodySmallEmphasized>
            </XStack>
            <YStack gap={32}>
                {Object.entries(holisticSummary ?? {}).map(
                    ([category, summary]) => {
                        return (
                            <HolisticCategoryCard
                                key={category}
                                category={category as HolisticSummaryCategory}
                                summary={summary}
                                membershipDetails={membershipDetails}
                                setEditingAccount={setEditingAccount}
                                setDeletingAccount={setDeletingAccount}
                                setMergingAccount={setMergingAccount}
                                setApexWithdrawalAccount={
                                    setApexWithdrawalAccount
                                }
                                setApexDepositAccount={setApexDepositAccount}
                                setSelectedCreateAccountCategory={
                                    setSelectedCreateAccountCategory
                                }
                                hasAnySpinwheelAccounts={
                                    hasAnySpinwheelAccounts
                                }
                            />
                        );
                    },
                )}
            </YStack>
            {editingAccount && (
                <AccountEditModal
                    account={editingAccount}
                    isOpen={true}
                    onClose={() => {
                        setEditingAccount(null);
                    }}
                />
            )}
            {deletingAccount && (
                <AccountDeleteModal
                    account={deletingAccount}
                    isOpen={true}
                    onClose={() => {
                        setDeletingAccount(null);
                    }}
                />
            )}
            {mergingAccount && (
                <AccountMergeModal
                    account={mergingAccount}
                    membershipDetails={membershipDetails}
                    isOpen={true}
                    onClose={() => {
                        setMergingAccount(null);
                    }}
                />
            )}
            {apexWithdrawalAccount && (
                <ApexWithdrawalModal
                    membership={membershipDetails}
                    account={apexWithdrawalAccount}
                    isOpen={true}
                    onClose={() => {
                        setApexWithdrawalAccount(null);
                    }}
                />
            )}
            {apexDepositAccount && (
                <ApexDepositModal
                    membership={membershipDetails}
                    account={apexDepositAccount}
                    isOpen={true}
                    onClose={() => {
                        setApexDepositAccount(null);
                    }}
                />
            )}
            {selectedCreateAccountCategory !== null && (
                <AccountCreateModal
                    isOpen={true}
                    defaultCategory={selectedCreateAccountCategory}
                    onClose={() => {
                        setSelectedCreateAccountCategory(null);
                    }}
                />
            )}
        </YStack>
    );
};

const HolisticCategoryCard = ({
    category,
    summary,
    membershipDetails,
    setEditingAccount,
    setDeletingAccount,
    setMergingAccount,
    setApexWithdrawalAccount,
    setApexDepositAccount,
    setSelectedCreateAccountCategory,
    hasAnySpinwheelAccounts,
}: {
    category: HolisticSummaryCategory;
    summary: CategorySummary;
    membershipDetails: MembershipDetails;
    setEditingAccount: (a: AccountSummary) => void;
    setDeletingAccount: (a: AccountSummary) => void;
    setMergingAccount: (a: AccountSummary) => void;
    setApexWithdrawalAccount: (a: AccountSummary) => void;
    setApexDepositAccount: (a: AccountSummary) => void;
    setSelectedCreateAccountCategory: (h: AccountCategory) => void;
    hasAnySpinwheelAccounts: boolean;
}) => {
    // Calculate aggregate minimum payment for borrow category
    const { aggregateMinimumPayment, hasMissingMinimumPayments } =
        useMemo(() => {
            if (category !== AccountCategory.BORROW) {
                return {
                    aggregateMinimumPayment: null,
                    hasMissingMinimumPayments: false,
                };
            }
            let total = 0;
            let hasMissing = false;
            summary.accounts.forEach((account) => {
                if (account.minimumPayment == null) {
                    hasMissing = true;
                }
                total += account.minimumPayment ?? 0;
            });
            return {
                aggregateMinimumPayment: total,
                hasMissingMinimumPayments: hasMissing,
            };
        }, [category, summary.accounts]);

    return (
        <Fieldset
            toggleable
            collapsed={summary.accounts.length < 1}
            collapseIcon={<Icon icon={ChevronDownIcon} />}
            expandIcon={<Icon icon={ChevronRightIcon} />}
            legend={
                <XStack
                    alignItems="center"
                    gap={8}
                    paddingLeft={16}
                    minWidth={225}
                >
                    <Icon size={18} icon={AccountCategoryIcons[category]} />
                    <BodyLargeEmphasized>
                        {capitalize(category)}:
                    </BodyLargeEmphasized>
                    <BodyMediumEmphasized>
                        {centsToFormattedDollars(
                            summary.totalCurrentBalanceCents ?? 0,
                            false,
                        )}
                    </BodyMediumEmphasized>
                </XStack>
            }
        >
            <YStack gap={16}>
                <XStack width="100%" justifyContent="flex-end" marginTop={-40}>
                    <XStack gap={8} alignItems="center">
                        <BodySmallEmphasized>
                            {summary.accounts.length === 1
                                ? '1 account'
                                : `${summary.accounts.length} accounts`}
                        </BodySmallEmphasized>

                        <IconButton
                            variant="tertiary"
                            icon={PlusIcon}
                            onPress={() => {
                                setSelectedCreateAccountCategory(category);
                            }}
                        />
                    </XStack>
                </XStack>
                {summary.accounts.length > 0 &&
                    category !== AccountCategory.SPEND && (
                        <AccountsTable
                            accounts={summary.accounts}
                            membershipDetails={membershipDetails}
                            setEditingAccount={setEditingAccount}
                            setDeletingAccount={setDeletingAccount}
                            setMergingAccount={setMergingAccount}
                            setApexWithdrawalAccount={setApexWithdrawalAccount}
                            setApexDepositAccount={setApexDepositAccount}
                            hasAnySpinwheelAccounts={hasAnySpinwheelAccounts}
                            category={category}
                        />
                    )}
                {summary.accounts.length > 0 &&
                    category === AccountCategory.BORROW &&
                    aggregateMinimumPayment != null && (
                        <XStack gap={8} paddingTop={8} alignItems="center">
                            <BodySmallEmphasized>
                                Total Minimum Payments:
                            </BodySmallEmphasized>
                            <BadgeLabel
                                variant={
                                    hasMissingMinimumPayments
                                        ? 'warning'
                                        : 'neutral'
                                }
                                size="regular"
                                text={centsToFormattedDollars(
                                    aggregateMinimumPayment,
                                )}
                            />
                            {hasMissingMinimumPayments && (
                                <FootnoteRegular color="$gray1000">
                                    (Some accounts have no minimum payment
                                    specified)
                                </FootnoteRegular>
                            )}
                        </XStack>
                    )}
                {summary.accounts.length > 0 &&
                    category === AccountCategory.SPEND && (
                        <SpendAccountsTables
                            summary={summary}
                            membershipDetails={membershipDetails}
                            setEditingAccount={setEditingAccount}
                            setDeletingAccount={setDeletingAccount}
                            setMergingAccount={setMergingAccount}
                            hasAnySpinwheelAccounts={hasAnySpinwheelAccounts}
                        />
                    )}
            </YStack>
        </Fieldset>
    );
};

const SpendAccountsTables = ({
    summary,
    membershipDetails,
    setEditingAccount,
    setDeletingAccount,
    setMergingAccount,
    hasAnySpinwheelAccounts,
}: {
    summary: CategorySummary;
    membershipDetails: MembershipDetails;
    setEditingAccount: (a: AccountSummary) => void;
    setDeletingAccount: (a: AccountSummary) => void;
    setMergingAccount?: (a: AccountSummary) => void;
    hasAnySpinwheelAccounts: boolean;
}) => {
    const cashAccounts = summary.accounts.filter(
        (a) => !isLiabilityAccountType(a.type ?? a.rawType ?? ''),
    );
    const creditAccounts = summary.accounts.filter((a) =>
        isLiabilityAccountType(a.type ?? a.rawType ?? ''),
    );
    const totalCashBalanceCents = cashAccounts.reduce(
        (total, account) => total + (account.currentBalanceCents ?? 0),
        0,
    );
    const totalCreditBalanceCents =
        creditAccounts.reduce(
            (total, account) => total + (account.currentBalanceCents ?? 0),
            0,
        ) * -1; // Credit balances are listed as positive, but invert the total to compare to the cash total

    return (
        <>
            <YStack>
                <GroupHeader
                    spendAccountCategory={SpendAccountCategory.CASH}
                    totalBalanceCents={totalCashBalanceCents}
                />
                <AccountsTable
                    accounts={cashAccounts}
                    membershipDetails={membershipDetails}
                    setEditingAccount={setEditingAccount}
                    setDeletingAccount={setDeletingAccount}
                    setMergingAccount={setMergingAccount}
                    hasAnySpinwheelAccounts={hasAnySpinwheelAccounts}
                />
            </YStack>
            <YStack paddingTop={16}>
                <GroupHeader
                    spendAccountCategory={SpendAccountCategory.CREDIT}
                    totalBalanceCents={totalCreditBalanceCents}
                />
                <AccountsTable
                    accounts={creditAccounts}
                    membershipDetails={membershipDetails}
                    setEditingAccount={setEditingAccount}
                    setDeletingAccount={setDeletingAccount}
                    setMergingAccount={setMergingAccount}
                    hasAnySpinwheelAccounts={hasAnySpinwheelAccounts}
                />
            </YStack>
        </>
    );
};

const GroupHeader = ({
    spendAccountCategory,
    totalBalanceCents,
}: {
    spendAccountCategory: SpendAccountCategory;
    totalBalanceCents: number;
}) => {
    const icon =
        spendAccountCategory != null
            ? SpendCategoryIcons[spendAccountCategory]
            : null;
    return (
        <XStack alignItems="center" gap={8} paddingTop={8} paddingBottom={12}>
            {icon != null && <Icon size={18} icon={icon} />}
            <BodyMediumEmphasized>
                {capitalize(spendAccountCategory)}:
            </BodyMediumEmphasized>
            <BodyMediumEmphasized>
                {centsToFormattedDollars(totalBalanceCents, false)}
            </BodyMediumEmphasized>
        </XStack>
    );
};

const isAccountMerged = (account: AccountSummary): boolean => {
    // Only Spinwheel and Quiltt accounts can be merged
    if (![Provider.SPINWHEEL, Provider.QUILTT].includes(account.provider)) {
        return false;
    }

    // A merged account has both spinwheelAccount and quilttAccount properties
    // These are added by the backend when accounts are merged
    const accountWithExtras = account as AccountSummary & {
        spinwheelAccount?: unknown;
        quilttAccount?: unknown;
    };

    return (
        accountWithExtras.spinwheelAccount != null &&
        accountWithExtras.quilttAccount != null
    );
};

const EditButtonRenderer = (
    account: AccountSummary,
    onEdit: (a: AccountSummary) => void,
    onDelete: (a: AccountSummary) => void,
    onMerge?: (a: AccountSummary) => void,
) => {
    const isMerged = isAccountMerged(account);

    return (
        <XStack>
            <IconButton
                variant="tertiary"
                icon={EditIcon}
                onPress={() => {
                    onEdit(account);
                }}
                disabled={account.provider === Provider.APEX}
            />
            {onMerge &&
                [Provider.SPINWHEEL, Provider.QUILTT].includes(
                    account.provider,
                ) && (
                    <IconButton
                        variant="tertiary"
                        tone={isMerged ? 'positive' : 'neutral'}
                        icon={MergeIcon}
                        onPress={() => {
                            onMerge(account);
                        }}
                    />
                )}
            {[Provider.QUILTT, Provider.MANUAL].includes(account.provider) && (
                <IconButton
                    variant="tertiary"
                    icon={TrashIcon}
                    onPress={() => {
                        onDelete(account);
                    }}
                />
            )}
        </XStack>
    );
};

const AggregatorIconRenderer = (account: AccountSummary) => {
    switch (account.provider) {
        case Provider.MANUAL:
            return (
                <div title="Manually added account">
                    <Icon color="$dropletBlue1100" icon={UserCheckIcon} />
                </div>
            );
        case Provider.QUILTT:
            return (
                <div style={{ width: 20, height: 20 }} title="Quiltt account">
                    <img
                        src="https://d1pwidzl9kib4u.cloudfront.net/grove-icons/quiltt_logo.png"
                        alt="Quiltt"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </div>
            );
        case Provider.ORION:
            return (
                <div title="Fruitful Orion account">
                    <Icon color="$green900" icon={FruitfulLogoIcon} />
                </div>
            );
        case Provider.SYNCTERA:
            return (
                <div title="Fruitful Synctera account">
                    <Logo width={20} height={20} />
                </div>
            );
        case Provider.APEX:
            return (
                <div title="Fruitful Apex account">
                    <Logo width={20} height={20} />
                </div>
            );
        case Provider.SPINWHEEL:
            return (
                <div
                    style={{ width: 20, height: 20 }}
                    title="Spinwheel account"
                >
                    <img
                        src="https://d1pwidzl9kib4u.cloudfront.net/grove-icons/Spinwheel-Icon-RGB.png"
                        alt="Spinwheel"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </div>
            );
    }
};

const AccountsTable = ({
    accounts,
    membershipDetails,
    setEditingAccount,
    setDeletingAccount,
    setMergingAccount,
    setApexWithdrawalAccount,
    setApexDepositAccount,
    hasAnySpinwheelAccounts,
    category,
}: {
    accounts: AccountSummary[];
    membershipDetails: MembershipDetails;
    setEditingAccount: (a: AccountSummary) => void;
    setDeletingAccount: (a: AccountSummary) => void;
    setMergingAccount?: (a: AccountSummary) => void;
    setApexWithdrawalAccount?: (a: AccountSummary) => void;
    setApexDepositAccount?: (a: AccountSummary) => void;
    hasAnySpinwheelAccounts: boolean;
    category?: HolisticSummaryCategory;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const showAccountLinkDetails = [
        AdminUserRole.ENGINEER,
        AdminUserRole.MEMBER_OPS,
        AdminUserRole.SUPERADMIN,
    ].includes(adminUser.role);

    const [selectedRow, setSelectedRow] = useState<AccountSummary | null>(null);

    const _OwnershipRenderer = (accountSummary: AccountSummary) => (
        <OwnershipRenderer
            members={membershipDetails.users}
            membershipType={membershipDetails.membershipType}
            accountSummary={accountSummary}
        />
    );

    return (
        <View>
            <DataTable<AccountSummary[]>
                value={accounts}
                tableStyle={{ width: '100%' }}
                sortField="currentBalanceCents"
                sortOrder={-1}
                size="small"
                emptyMessage="0 accounts"
                showHeaders={accounts.length > 0}
                selectionMode="single"
                isDataSelectable={(event: DataTableDataSelectableEvent) => {
                    return (
                        category === AccountCategory.INVEST &&
                        event.data.provider === Provider.APEX
                    );
                }}
                onRowSelect={(event: DataTableSelectEvent) => {
                    const isAccountSummary = (row): row is AccountSummary =>
                        'provider' in row;

                    if (isAccountSummary(event.data)) {
                        setSelectedRow(event.data);
                    }
                }}
            >
                <Column
                    body={AggregatorIconRenderer}
                    style={{ width: '40px' }}
                />

                <Column
                    field="institutionName"
                    header="Inst"
                    sortable
                    body={InstitutionRenderer}
                />
                <Column
                    field="name"
                    header="Account ⓘ"
                    headerTooltip="Account name provided by institution or nicknamed by member"
                    headerTooltipOptions={{ position: 'top' }}
                    sortable
                    body={NameRenderer}
                />
                <Column
                    field="userId"
                    header="Ownership"
                    headerTooltip="For external accounts, the member who linked it and its status. For Fruitful accounts, the member(s) who have access to it."
                    headerTooltipOptions={{ position: 'top' }}
                    sortable
                    body={_OwnershipRenderer}
                />
                <Column
                    field="currentBalanceCents"
                    header="Balance"
                    sortable
                    align="right"
                    body={AmountRenderer}
                />
                <Column
                    field="lastUpdated"
                    header="Last updated ⓘ"
                    headerTooltip="The date we last received balance and transactions data"
                    headerTooltipOptions={{ position: 'top' }}
                    sortable
                    body={LastUpdatedRenderer}
                />
                <Column
                    field="type"
                    header="Acct type ⓘ"
                    headerTooltip="The type of account as provided by the institution. We do not collect this from members, and so we do not have a real value to show for manual accounts."
                    headerTooltipOptions={{ position: 'top' }}
                    body={(data: AccountSummary) => {
                        return (
                            <AccountTypeRenderer
                                as={data}
                                showAllAccountTypes={showAccountLinkDetails}
                            />
                        );
                    }}
                />
                <Column
                    field="interestRate"
                    header="Interest Rate"
                    body={InterestRateRenderer}
                />
                <Column
                    field="minimumPayment"
                    header="Minimum Payment"
                    body={MinimumPaymentRenderer}
                />
                <Column
                    field="description"
                    header="Description"
                    body={DescriptionRenderer}
                />
                <Column
                    field="provider"
                    header="Aggregator info ⓘ"
                    headerTooltip="The service that enables our connection to the institution/account, or 'Manual' if the account was added manually."
                    headerTooltipOptions={{ position: 'top' }}
                    sortable
                    body={AggregatorRenderer}
                    hidden={!showAccountLinkDetails}
                />
                <Column
                    body={(rowData) =>
                        EditButtonRenderer(
                            rowData as AccountSummary,
                            setEditingAccount,
                            setDeletingAccount,
                            hasAnySpinwheelAccounts
                                ? setMergingAccount
                                : undefined,
                        )
                    }
                    style={{ width: '40px' }}
                />
            </DataTable>
            <GroveSimpleModal
                isOpen={selectedRow != null}
                onClose={() => {
                    setSelectedRow(null);
                }}
                renderInAppRoot={false}
            >
                <InvestmentAccountTable
                    account={selectedRow}
                    onApexDeposit={
                        setApexDepositAccount != null
                            ? (account) => {
                                  setSelectedRow(null);
                                  setApexDepositAccount(account);
                              }
                            : undefined
                    }
                    onApexWithdrawal={
                        setApexWithdrawalAccount != null
                            ? (account) => {
                                  setSelectedRow(null);
                                  setApexWithdrawalAccount(account);
                              }
                            : undefined
                    }
                />
            </GroveSimpleModal>
        </View>
    );
};

const InstitutionRenderer = ({
    institutionName,
    accountNumberMasked,
}: AccountSummary) => (
    <YStack gap={2}>
        <BodySmallRegular>{institutionName}</BodySmallRegular>
        {!isEmpty(accountNumberMasked) && (
            <MonospaceDetailText>*{accountNumberMasked}</MonospaceDetailText>
        )}
    </YStack>
);

const AmountRenderer = ({ currentBalanceCents }: AccountSummary) => (
    <MonospaceNumericText>
        {currentBalanceCents != null
            ? centsToFormattedDollars(currentBalanceCents)
            : ''}
    </MonospaceNumericText>
);

const MinimumPaymentRenderer = ({ minimumPayment }: AccountSummary) => (
    <MonospaceNumericText>
        {minimumPayment != null ? centsToFormattedDollars(minimumPayment) : ''}
    </MonospaceNumericText>
);

const InterestRateRenderer = ({ interestRate }: AccountSummary) => (
    <MonospaceNumericText>
        {interestRate != null ? `${interestRate}%` : ''}
    </MonospaceNumericText>
);

const DescriptionRenderer = ({ description }: AccountSummary) => {
    const maxLength = 50;
    const shouldShowTooltip = (description?.length ?? 0) > maxLength;

    return (
        // @ts-expect-error: disable is functional and there is no other valid prop to use
        <Tooltip placement="top" delay={0} disable={!shouldShowTooltip}>
            <Tooltip.Trigger>
                <XStack maxWidth={300} gap={8} alignItems="center">
                    <BodySmallRegular numberOfLines={1}>
                        {description}
                    </BodySmallRegular>
                </XStack>
            </Tooltip.Trigger>
            <Tooltip.Content
                style={{
                    maxWidth: 300,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: 'black',
                }}
            >
                <BodySmallRegular color="white">{description}</BodySmallRegular>
            </Tooltip.Content>
        </Tooltip>
    );
};

const NameRenderer = (accountSummary: AccountSummary) => {
    const { apexPortfolio, apexRiskLevel, name, provider } = accountSummary;
    if (
        provider === Provider.APEX &&
        apexRiskLevel != null &&
        apexPortfolio != null
    ) {
        const portfolio = apexPortfolio === 'CORE' ? 'Core' : 'ESG';
        return (
            <YStack gap={4}>
                <BodySmallEmphasized>{name}</BodySmallEmphasized>
                <LabelRegular color="$gray900">
                    ({portfolio} {apexRiskLevel})
                </LabelRegular>
            </YStack>
        );
    }
    return <BodySmallEmphasized>{name}</BodySmallEmphasized>;
};

const LastUpdatedRenderer = (accountSummary: AccountSummary) => {
    const lastUpdated = accountSummary.lastUpdated;
    return (
        <BodySmallRegular>
            {lastUpdated != null
                ? formatToSimpleDateString(lastUpdated)
                : 'Unknown'}
        </BodySmallRegular>
    );
};

const OwnershipRenderer = ({
    members,
    membershipType,
    accountSummary,
}: {
    members: MemberDetails[];
    membershipType: MembershipType;
    accountSummary: AccountSummary;
}) => {
    const { provider, primaryOwnerUserId, jointOwnerUserId, type } =
        accountSummary;

    if (provider === Provider.SYNCTERA) {
        if (jointOwnerUserId !== undefined) {
            return <BodySmallRegular>Joint</BodySmallRegular>;
        } else {
            const member = members.find((m) => m.id === primaryOwnerUserId);
            return <BodySmallRegular>{member?.firstName}</BodySmallRegular>;
        }
    } else if (provider === Provider.APEX) {
        if (
            type === SupportedApexAccountRegistrationType.RegistrationJointWros
        ) {
            return <BodySmallRegular>Joint</BodySmallRegular>;
        } else {
            const { userId } = accountSummary;
            const member = members.find((m) => m.id === userId);
            return <BodySmallRegular>{member?.firstName}</BodySmallRegular>;
        }
    } else {
        return (
            <LinkStateRenderer
                members={members}
                membershipType={membershipType}
                accountSummary={accountSummary}
            />
        );
    }
};

const LinkStateRenderer = ({
    members,
    membershipType,
    accountSummary,
}: {
    members: MemberDetails[];
    membershipType: MembershipType;
    accountSummary: AccountSummary;
}) => {
    const { userId, provider } = accountSummary;
    const getLinkState = (provider: Provider): LinkState | null => {
        switch (provider) {
            case Provider.QUILTT:
            case Provider.ORION:
                return LinkState.CONNECTED;
            default:
                return null;
        }
    };

    const shouldShowMember =
        membershipType !== MembershipType.SOLO && userId !== undefined;
    const member = shouldShowMember
        ? members.find((m) => m.id === userId)
        : undefined;
    const state = getLinkState(provider);

    if (state === null) return null;

    return (
        <XStack gap={8} alignItems="center">
            {
                <PRButton // Not really a button but this is an easy way to get an icon with a tooltip
                    rounded
                    text
                    tooltip={capitalize(state)}
                    style={{
                        backgroundColor: LinkStateColor[state],
                        padding: 4,
                    }}
                >
                    <Icon icon={LinkStateIcon[state]} size={14} />
                </PRButton>
            }
            {shouldShowMember && (
                <YStack>
                    <MonospaceDetailText>Linked by:</MonospaceDetailText>
                    <MonospaceDetailText>
                        {member?.firstName ?? userId}
                    </MonospaceDetailText>
                </YStack>
            )}
        </XStack>
    );
};

const AggregatorRenderer = (rowData: unknown) => {
    const account = rowData as AccountSummary;
    const accountWithExtras = account as AccountSummary & {
        spinwheelAccount?: { id: string };
        quilttAccount?: { id: string };
    };

    const isMerged =
        accountWithExtras.spinwheelAccount != null &&
        accountWithExtras.quilttAccount != null;

    // Get Spinwheel Account ID - if main account is Quiltt, get from spinwheelAccount, otherwise use account.id
    const spinwheelAccountId =
        account.provider === Provider.QUILTT
            ? accountWithExtras.spinwheelAccount?.id
            : account.id;

    return (
        <YStack gap={4} maxWidth={100}>
            <MonospaceDetailText>{account.provider}</MonospaceDetailText>
            <MonospaceDetailText wordWrap="break-word">
                {account.provider === Provider.ORION ? '' : account.id}
            </MonospaceDetailText>
            {isMerged &&
                spinwheelAccountId != null &&
                spinwheelAccountId !== '' && (
                    <MonospaceDetailText
                        wordWrap="break-word"
                        color="$sweetViolet1100"
                    >
                        Spinwheel: {spinwheelAccountId}
                    </MonospaceDetailText>
                )}
        </YStack>
    );
};

const AccountTypeRenderer = ({
    as,
    showAllAccountTypes,
}: {
    as: AccountSummary;
    showAllAccountTypes: boolean;
}) => {
    const { type, rawType, fruitfulAccountType, provider } = as;
    const formatAccountType = (input: string, provider: Provider) => {
        if (provider === Provider.MANUAL) return '(Manual)';
        if (provider === Provider.APEX) {
            return (
                ReadableApexAccountRegistrationType[input] ?? '(Unspecified)'
            );
        }
        if (fruitfulAccountType != null) {
            return (
                <FruitfulAccountTypeRenderer
                    accountSummary={as}
                    fruitfulAccountType={fruitfulAccountType}
                />
            );
        }

        // Spinwheel and quiltt types are not accurate enough for guide usage
        // so don't show them.
        if (!showAllAccountTypes) return;

        const prefixes = ['depository', 'liability', 'investment'];
        const acronyms = ['IRA', 'HSA', 'CD'];
        const typeParts = input.split(/[._]/);
        if (
            provider !== Provider.QUILTT &&
            prefixes.includes((typeParts[0] ?? '').toLowerCase())
        ) {
            typeParts[0] = '';
        }
        const rejoinedParts = capitalize(typeParts.join(' ').trim());
        return acronyms.includes(rejoinedParts.toUpperCase())
            ? rejoinedParts.toUpperCase()
            : rejoinedParts;
    };

    const accountType = formatAccountType(type ?? rawType ?? '', provider);
    return <BodySmallRegular>{accountType}</BodySmallRegular>;
};

const FruitfulAccountTypeRenderer = ({
    accountSummary,
    fruitfulAccountType,
}: {
    accountSummary: AccountSummary;
    fruitfulAccountType: AccountType;
}) => {
    const readableAccountType =
        ReadableFruitfulAccountTypes[fruitfulAccountType];

    if (accountSummary.isAutopayEnabled != null) {
        const physicalCards =
            accountSummary.cards?.filter(
                (c) => c.form === 'PHYSICAL' && c.cardStatus === 'ACTIVE',
            ) ?? [];
        const virtualCards =
            accountSummary.cards?.filter(
                (c) => c.form === 'VIRTUAL' && c.cardStatus === 'ACTIVE',
            ) ?? [];
        const tooltip = `Autopay ${accountSummary.isAutopayEnabled ? 'enabled' : 'disabled'}, ${physicalCards.length} physical, ${virtualCards.length} virtual`;
        return (
            <TooltipTrigger tooltipContent={tooltip}>
                <XStack gap={8}>
                    <BodyMediumRegular>{readableAccountType}</BodyMediumRegular>
                    <Icon
                        icon={CreditCardIcon}
                        color={
                            accountSummary.isAutopayEnabled
                                ? '$green1000'
                                : '$red1000'
                        }
                    />
                </XStack>
            </TooltipTrigger>
        );
    }
    return readableAccountType;
};

const isLiabilityAccountType = (accountType?: string): boolean =>
    (accountType ?? '').toLowerCase().includes('liability') ||
    (accountType ?? '').toLowerCase().includes('loan') ||
    (accountType ?? '').toLowerCase() === 'credit';

const LinkStateIcon: Record<LinkState, IconType> = {
    [LinkState.CONNECTING]: HourglassIcon,
    [LinkState.AWAITING_TOKEN_EXCHANGE]: HourglassIcon,
    [LinkState.CONNECTED]: LinkIcon,
    [LinkState.DELETED]: TrashIcon,
    [LinkState.ERROR]: CircleAlertIcon,
    [LinkState.UNKNOWN]: CircleQuestionMarkIcon,
};

const LinkStateColor: Record<LinkState, string> = {
    [LinkState.CONNECTING]: tokens.color.light.orange.orange400,
    [LinkState.AWAITING_TOKEN_EXCHANGE]: tokens.color.light.orange.orange400,
    [LinkState.CONNECTED]: tokens.color.light.green.green300,
    [LinkState.DELETED]: tokens.color.light.gray.gray100,
    [LinkState.ERROR]: tokens.color.light.red.red100,
    [LinkState.UNKNOWN]: tokens.color.light.orange.orange400,
};

const ReadableFruitfulAccountTypes: Record<AccountType, string | undefined> = {
    [AccountType.SAVING]: 'Savings',
    [AccountType.CHECKING]: 'Cash',
    [AccountType.CHARGE_SECURED]: undefined,
    [AccountType.CHARGE_UNSECURED]: undefined,
    [AccountType.GENERAL_LEDGER]: undefined,
    [AccountType.LINE_OF_CREDIT]: undefined,
};
